import React from "react";
// eslint-disable-next-line no-unused-vars
import { notification, NotificationArgsProps } from "antd";

import {
   SuccessWorkspace,
   ErrorWorkspace,
   OrganizationOfficeBuildingGreen,
   OrganizationOfficeBuildingRed,
   SuccessProfile,
   ErrorProfile,
   DeleteSurveySuccessfully,
   ErrorDeletedSurvey,
   RequestSuccess,
   RequestError,
   LockSuccessIcon,
   LockErrorIcon,
   SuccessDeleteAssessment,
   ErrorDeletingAssessment,
   TicketSuccess,
   TicketError,
   UserBlueIcon,
   UserRedIcon,
   TaskSuccess,
   TaskError
} from "../../assets/icons";

/**
 * @typedef {"workspace" | "organization" | "team-member" | "survey" | "request" | "request-lock" | "assessment" | "ticket" | "user" | "task"} IconType
 */

const notificationProps = {
   placement: "top",
   style: {
      width: "780px",
      minHeight: "101px",
      padding: 0
   }
};

const iconMap = new Map([
   ["organization-success", OrganizationOfficeBuildingGreen],
   ["organization-error", OrganizationOfficeBuildingRed],
   ["workspace-success", SuccessWorkspace],
   ["workspace-error", ErrorWorkspace],
   ["team-member-success", SuccessProfile],
   ["team-member-error", ErrorProfile],
   ["survey-success", DeleteSurveySuccessfully],
   ["survey-error", ErrorDeletedSurvey],
   ["request-success", RequestSuccess],
   ["request-error", RequestError],
   ["request-lock-success", LockSuccessIcon],
   ["request-lock-error", LockErrorIcon],
   ["assessment-success", SuccessDeleteAssessment],
   ["assessment-error", ErrorDeletingAssessment],
   ["ticket-success", TicketSuccess],
   ["ticket-error", TicketError],
   ["user-success", UserBlueIcon],
   ["user-error", UserRedIcon],
   ["task-success", TaskSuccess],
   ["task-error", TaskError]
]);

/**
 * Custom notifications hook with predefined icons for success and error cases
 * @example
 * function App() {
 *    const { api, notificationContextHolder } = useNotification();
 *
 *    return (
 *       <Fragment>
 *          {notificationContextHolder}
 *          <Button onClick={() => api.success("organization", { duration: 5 })}>Notify</Button>
 *          // ... rest of the JSX
 *       </Fragment>
 *    )
 * }
 * @returns
 */
export function useNotification() {
   const [api, contextHolder] = notification.useNotification({
      duration: 3,
      prefixCls: "custom-notification",
      stack: 2
   });

   /**
    * Function to handle success notifications.
    * @param {IconType} iconType - Type of icon to display. You can still override this in the second param.
    * @param {NotificationArgsProps} args - Additional arguments to pass to the success notification.
    */
   const success = (iconType, args) => {
      api.success({
         ...notificationProps,
         icon: (
            <img
               src={iconMap.get(`${iconType}-success`)}
               alt={`${iconType} success notification`}
            />
         ),
         ...args
      });
   };

   /**
    * Function to handle success notifications.
    * @param {IconType} iconType - Type of icon to display. You can still override this in the second param.
    * @param {NotificationArgsProps} args - Additional arguments to pass to the success notification.
    */
   const error = (iconType, args) => {
      api.error({
         ...notificationProps,
         icon: (
            <img src={iconMap.get(`${iconType}-error`)} alt={`${iconType} error notification`} />
         ),
         ...args
      });
   };

   return {
      notificationContextHolder: contextHolder,
      api: {
         success,
         error
      }
   };
}
